import React from "react";
import EcoIcon from "./ecoIcon";
import { ECOSYSTEM, ECO_TINTS } from "./ecosystemData";

// ---- compact product mocks (the "what flows" preview inside each card) ----
const MockPM = ({ tint }) => {
    const rows = [
        { l: "Kickoff", w: "100%", done: true },
        { l: "Discovery", w: "100%", done: true },
        { l: "Build phase", w: "62%", done: false },
    ];
    return (
        <div className="px-3 pb-3 pt-2.5">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[8.5px] font-bold tracking-[0.2em] text-grey45 font-body">SPRINT · 04</div>
                <div className="flex gap-[3px]">
                    <span className="w-1 h-1 rounded-full" style={{ background: "#D2D6DA" }} />
                    <span className="w-1 h-1 rounded-full" style={{ background: tint.ink }} />
                    <span className="w-1 h-1 rounded-full" style={{ background: "#D2D6DA" }} />
                </div>
            </div>
            {rows.map((r) => (
                <div key={r.l} className="mb-1.5">
                    <div className="flex justify-between items-center mb-[3px]">
                        <span className="text-[9px] text-blue70">{r.l}</span>
                        <span className="text-[7.5px] font-bold tracking-[0.12em] font-body" style={{ color: r.done ? tint.ink : "#9B9FA8" }}>
                            {r.done ? "DONE" : r.w === "0%" ? "—" : "IN FLIGHT"}
                        </span>
                    </div>
                    <div className="h-[3px] rounded-full" style={{ background: "#F0F1F4" }}>
                        <div className="h-full rounded-full" style={{ width: r.w, background: tint.ink, opacity: r.done ? 1 : 0.7 }} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const MockOps = ({ tint }) => (
    <div className="px-3 pb-3 pt-2.5">
        <div className="text-[8.5px] font-bold tracking-[0.2em] text-grey45 font-body mb-2.5">OPERATING MODEL</div>
        <svg width="100%" height="74" viewBox="0 0 180 74">
            <line x1="90" y1="10" x2="30" y2="40" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <line x1="90" y1="10" x2="90" y2="40" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <line x1="90" y1="10" x2="150" y2="40" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <line x1="30" y1="54" x2="14" y2="70" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <line x1="30" y1="54" x2="46" y2="70" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <line x1="150" y1="54" x2="134" y2="70" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <line x1="150" y1="54" x2="166" y2="70" stroke={tint.line} strokeWidth="1" strokeDasharray="2 3" />
            <rect x="74" y="0" width="32" height="13" rx="3" fill={tint.ink} />
            <text x="90" y="9" textAnchor="middle" fontFamily="IBM Plex Sans" fontSize="6.5" fontWeight="700" letterSpacing="0.8" fill="#fff">CORE</text>
            {[[14, 40, "PEOPLE"], [74, 40, "TOOLS"], [134, 40, "PROCESS"]].map(([x, y, l]) => (
                <g key={l}>
                    <rect x={x} y={y} width="32" height="13" rx="3" fill="#fff" stroke={tint.line} />
                    <text x={Number(x) + 16} y={Number(y) + 8.5} textAnchor="middle" fontFamily="IBM Plex Sans" fontSize="5.5" fontWeight="700" letterSpacing="0.5" fill={tint.ink}>{l}</text>
                </g>
            ))}
            {[0, 32, 120, 152].map((x, i) => (
                <rect key={i} x={x} y="65" width="28" height="6" rx="2" fill={tint.bg} stroke={tint.line} strokeWidth="0.5" />
            ))}
        </svg>
    </div>
);

const MockDD = ({ tint }) => (
    <div className="px-3 pb-4 pt-2.5">
        <div className="flex items-center justify-between mb-2.5">
            <div className="text-[8.5px] font-bold tracking-[0.2em] text-grey45 font-body">COMPONENT · v0.4</div>
            <div className="flex gap-[3px]">
                <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#E08A8A" }} />
                <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#E0BE8A" }} />
                <span className="w-[5px] h-[5px] rounded-full" style={{ background: "#8AB89A" }} />
            </div>
        </div>
        <div className="grid gap-[7px]">
            <div className="h-3.5 rounded flex items-center pl-1.5 gap-1" style={{ background: tint.bg, border: `1px solid ${tint.line}` }}>
                <span className="w-1 h-1 rounded-full" style={{ background: tint.ink }} />
                <span className="h-[3px] rounded-sm" style={{ width: 22, background: "#D2D6DA" }} />
                <span className="h-[3px] rounded-sm" style={{ width: 14, background: "#E0E2E6" }} />
            </div>
            <div className="h-[26px] rounded bg-white p-[5px]" style={{ border: `1px solid ${tint.line}` }}>
                <div className="h-[3px] rounded-sm mb-1" style={{ width: "40%", background: tint.ink }} />
                <div className="flex gap-[3px]">
                    <span className="flex-1 h-2.5 rounded-sm" style={{ background: tint.bg }} />
                    <span className="flex-1 h-2.5 rounded-sm" style={{ background: tint.bg }} />
                    <span className="flex-1 h-2.5 rounded-sm" style={{ background: tint.ink }} />
                </div>
            </div>
            <div className="h-[15px] mt-0.5 rounded flex items-center justify-center" style={{ background: tint.ink }}>
                <span className="text-[7.5px] font-bold tracking-[0.18em] text-white font-body">SHIP</span>
            </div>
        </div>
    </div>
);

const MOCKS = { fpm: MockPM, fops: MockOps, fdd: MockDD };

// ---- a single partner preview card (header + mock) ----
const PreviewCard = ({ service }) => {
    const tint = ECO_TINTS[service.tintKey];
    const Mock = MOCKS[service.id];
    return (
        <div
            className="bg-white rounded-xl overflow-hidden shadow-md w-full h-full"
            style={{ border: `1px solid ${tint.line}` }}
        >
            <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: tint.bg, borderBottom: `1px solid ${tint.line}` }}>
                <span className="w-[22px] h-[22px] rounded-md bg-white grid place-items-center shrink-0" style={{ border: `1px solid ${tint.line}` }}>
                    <EcoIcon name={service.icon} size={13} color={tint.ink} />
                </span>
                <div className="min-w-0">
                    <div className="text-[12px] font-bold text-blue10 leading-tight">{service.title}</div>
                    <div className="text-[8.5px] font-bold uppercase tracking-[0.18em] font-body mt-0.5" style={{ color: tint.ink }}>{service.label}</div>
                </div>
            </div>
            <Mock tint={tint} />
        </div>
    );
};

// ---- the animated orbital nucleus (fills its square parent) ----
const OrbitalNucleus = () => {
    const orbits = [
        { size: "60%", dur: "38s", delay: "-5s", c: "#A5C3EB" },
        { size: "76%", dur: "54s", delay: "-22s", c: "#A8AEDA" },
        { size: "92%", dur: "72s", delay: "-44s", c: "#B3BAC4" },
    ];
    return (
        <div className="relative w-full h-full">
            {/* pulsing halo */}
            <div
                className="eco-halo absolute left-1/2 top-1/2 rounded-full"
                style={{
                    width: "118%",
                    height: "118%",
                    background: "radial-gradient(circle, rgba(40,68,140,0.18) 0%, rgba(40,68,140,0.05) 55%, rgba(40,68,140,0) 75%)",
                }}
            />
            {/* orbit rings */}
            <svg viewBox="-100 -100 200 200" className="absolute inset-0 w-full h-full">
                <circle cx="0" cy="0" r="60" fill="none" stroke="#A5C3EB" strokeWidth="0.8" strokeDasharray="2 4" opacity="0.55" />
                <circle cx="0" cy="0" r="76" fill="none" stroke="#A8AEDA" strokeWidth="0.8" strokeDasharray="3 5" opacity="0.55" />
                <circle cx="0" cy="0" r="92" fill="none" stroke="#B3BAC4" strokeWidth="0.8" strokeDasharray="3 5" opacity="0.55" />
            </svg>
            {/* orbiting particles */}
            {orbits.map((o, i) => (
                <div
                    key={i}
                    className="eco-orbit absolute left-1/2 top-1/2"
                    style={{ width: o.size, height: o.size, animationDuration: o.dur, animationDelay: o.delay }}
                >
                    <span
                        className="absolute left-1/2 top-0 rounded-full"
                        style={{
                            width: 11, height: 11,
                            transform: "translate(-50%,-50%)",
                            background: o.c,
                            boxShadow: `0 0 12px ${o.c}aa, 0 0 2px ${o.c}`,
                            border: "1.5px solid #fff",
                        }}
                    />
                </div>
            ))}
            {/* outer rim */}
            <div
                className="absolute left-1/2 top-1/2 rounded-full"
                style={{
                    width: "56%", height: "56%",
                    transform: "translate(-50%,-50%)",
                    border: "1px solid rgba(15,20,36,0.08)",
                    boxShadow: "inset 0 0 0 4px rgba(255,255,255,0.65)",
                }}
            />
            {/* dark domed core */}
            <div
                className="absolute left-1/2 top-1/2 rounded-full grid place-items-center text-center overflow-hidden"
                style={{
                    width: "52%", height: "52%",
                    transform: "translate(-50%,-50%)",
                    background: "radial-gradient(circle at 50% 30%, #243069 0%, #182553 45%, #0A0F1F 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -12px 26px rgba(0,0,0,0.40), 0 12px 28px rgba(15,20,36,0.25)",
                }}
            >
                <svg viewBox="-50 -50 100 100" className="absolute inset-0 w-full h-full" style={{ opacity: 0.16 }}>
                    {[12, 22, 32, 42].map((r) => (
                        <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="#fff" strokeWidth="0.5" />
                    ))}
                </svg>
                <div className="relative">
                    <div className="font-light italic text-white/70 leading-none" style={{ fontSize: 11 }}>Your</div>
                    <div className="font-normal text-white leading-tight tracking-tight" style={{ fontSize: 22, marginTop: 2 }}>Org</div>
                   
                </div>
            </div>
        </div>
    );
};

// ---- desktop diagram geometry (viewBox 620 x 640) ----
// Taller canvas + a taller bottom card so the Design preview (longer title +
// SHIP button) has room to breathe instead of clipping at the card edge.
const W = 620, H = 640;
const CARD_W = 200, TOP_H = 196, BOT_H = 210;
const HUB = { x: 310, y: 300 };
const HUB_BUFFER = 100;
const CARDS = [
    { svc: ECOSYSTEM[0], left: 0, top: 0, w: CARD_W, h: TOP_H, anchor: { x: CARD_W, y: TOP_H / 2 } },
    { svc: ECOSYSTEM[1], left: W - CARD_W, top: 0, w: CARD_W, h: TOP_H, anchor: { x: 0, y: TOP_H / 2 } },
    { svc: ECOSYSTEM[2], left: (W - CARD_W) / 2, top: H - BOT_H, w: CARD_W, h: BOT_H, anchor: { x: CARD_W / 2, y: 0 } },
];
const LINES = CARDS.map((c) => {
    const ax = c.left + c.anchor.x;
    const ay = c.top + c.anchor.y;
    const dx = HUB.x - ax;
    const dy = HUB.y - ay;
    const len = Math.sqrt(dx * dx + dy * dy);
    return { x1: HUB.x - (dx / len) * HUB_BUFFER, y1: HUB.y - (dy / len) * HUB_BUFFER, x2: ax, y2: ay };
});
const pct = (n, total) => `${(n / total) * 100}%`;

const Ecosystem = () => {
    return (
        <section className="w-full container mx-auto px-6 md:px-12 lg:px-20 pt-16 lg:pt-28 pb-16 lg:pb-28">
            <style>{`
                @keyframes v7Orbit { from { transform: translate(-50%,-50%) rotate(0deg) } to { transform: translate(-50%,-50%) rotate(360deg) } }
                .eco-orbit { animation: v7Orbit linear infinite; transform: translate(-50%,-50%); }
                @keyframes v7HaloPulse { 0%,100% { opacity: .55; transform: translate(-50%,-50%) scale(1) } 50% { opacity: .85; transform: translate(-50%,-50%) scale(1.04) } }
                .eco-halo { animation: v7HaloPulse 6s ease-in-out infinite; transform: translate(-50%,-50%); }
                @media (prefers-reduced-motion: reduce) { .eco-orbit, .eco-halo { animation: none; } }
            `}</style>

            <div className="bg-white rounded-3xl border border-solid border-FGNeutralAccent shadow-sm p-8 md:p-14 lg:p-20 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                {/* Left — copy */}
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-primaryBrand font-body mb-4">Partnered Ecosystem</div>
                    <h2 className="text-3xl md:text-4xl font-normal tracking-tight text-blue10 leading-tight">
                        One partner. <br />
                        <span className="font-light italic text-primaryBrand">Every function covered.</span>
                    </h2>
                    <p className="text-base md:text-lg leading-relaxed text-blue70 mt-8 max-w-2xl">
                        Most partnerships start with one specialty. A fractional PM gets delivery under control. Operations builds the system underneath it. Design and development keeps the product moving.
                    </p>
                </div>

                {/* Right — diagram */}
                <div>
                    {/* Desktop: orbital diagram with connectors */}
                    <div className="hidden lg:block relative w-full max-w-[580px] mx-auto" style={{ aspectRatio: `${W} / ${H}` }}>
                        <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full">
                            <defs>
                                <pattern id="ecoDotGrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
                                    <circle cx="1" cy="1" r="0.8" fill="#DDE0E5" />
                                </pattern>
                                <radialGradient id="ecoHubGlow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#3D5099" stopOpacity="0.18" />
                                    <stop offset="55%" stopColor="#182553" stopOpacity="0.05" />
                                    <stop offset="100%" stopColor="#182553" stopOpacity="0" />
                                </radialGradient>
                            </defs>
                            <rect width={W} height={H} fill="url(#ecoDotGrid)" opacity="0.55" />
                            <circle cx={HUB.x} cy={HUB.y} r={198} fill="url(#ecoHubGlow)" />
                            {LINES.map((l, i) => {
                                const tint = ECO_TINTS[CARDS[i].svc.tintKey];
                                return (
                                    <g key={i}>
                                        <line x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke={tint.ink} strokeWidth="1.2" strokeDasharray="3 4" opacity="0.55" />
                                        <circle cx={l.x2} cy={l.y2} r="3.2" fill="#fff" stroke={tint.ink} strokeWidth="1.4" />
                                    </g>
                                );
                            })}
                        </svg>

                        {/* centered orbital nucleus */}
                        <div
                            className="absolute"
                            style={{ left: "50%", top: pct(HUB.y, H), transform: "translate(-50%,-50%)", width: "34%", aspectRatio: "1 / 1" }}
                        >
                            <OrbitalNucleus />
                        </div>

                        {/* preview cards */}
                        {CARDS.map((c) => (
                            <div
                                key={c.svc.id}
                                className="absolute"
                                style={{ left: pct(c.left, W), top: pct(c.top, H), width: pct(c.w, W), height: pct(c.h, H) }}
                            >
                                <PreviewCard service={c.svc} />
                            </div>
                        ))}
                    </div>

                    {/* Mobile / tablet: nucleus on top, cards stacked */}
                    <div className="lg:hidden flex flex-col items-center gap-8">
                        <div className="w-44 h-44">
                            <OrbitalNucleus />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 w-full">
                            {ECOSYSTEM.map((s) => (
                                <div key={s.id} className="h-[200px]">
                                    <PreviewCard service={s} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Ecosystem;
